// src/components/common/DevicesMap.js - WITH BEAUTIFUL CARD
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import {
  RefreshCw, Navigation, X,
  Thermometer, Droplets, Eye, Lock, MapPin, Clock, BarChart2,
  CheckCircle, MessageSquare, CreditCard, Radio,
} from 'lucide-react';
import { fetchWeatherDevices, fetchWithRetry } from '../../services/weatherService';
import { fetchSoilDevices } from '../../services/soilService';
import EnhancedAuthModal from './EnhancedAuthModal';
import 'leaflet/dist/leaflet.css';

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Prettify gateway IDs → readable short name
const prettyGateway = (id) => {
  if (!id) return null;
  // e.g. "su-wp-busia-butula-gateway" → "Butula GW"
  const clean = id.replace(/^(su-wp-|wp-)/, '').replace(/-gateway$/, '').replace(/-wirelessplanet$/, '');
  const words = clean.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1));
  return words.join(' ') + ' GW';
};

// Beautiful Station Card Component
const BeautifulStationCard = ({ device, isAuthenticated, onViewDashboard, isClosest, isSuperAdmin }) => {
  const gatewayName = prettyGateway(device.gatewayId);
  const fmt = (v, dec = 1) => v != null ? Number(v).toFixed(dec) : '—';

  return (
    <div className="min-w-[320px] bg-white rounded-2xl overflow-hidden shadow-2xl">

      {/* Header */}
      <div className="relative bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 p-5">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
        <div className="relative z-10">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-white font-bold text-lg leading-tight flex-1 pr-2">{device.name}</h3>
            <div className={`px-2.5 py-1 rounded-full text-xs font-bold shadow ${
              device.status === 'online'  ? 'bg-emerald-400 text-emerald-900' :
              device.status === 'warning' ? 'bg-amber-400 text-amber-900'    :
                                            'bg-rose-400 text-rose-900'
            }`}>
              {device.status === 'online' ? 'ONLINE' : device.status === 'warning' ? 'WARNING' : 'OFFLINE'}
            </div>
          </div>
          {device.location && (
            <div className="flex items-center gap-1 text-white/90 text-sm font-medium mb-2">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{device.location}</span>
            </div>
          )}
          <div className="flex items-center gap-3 text-white/80 text-xs flex-wrap">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{device.altitude ? `${device.altitude} m` : '—'}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{device.lastUpdate}</span>
            {gatewayName && (
              <span className="flex items-center gap-1 bg-white/20 px-1.5 py-0.5 rounded-full">
                <Eye className="w-3 h-3" />{gatewayName}
                {isSuperAdmin && device.rssi != null && <span className="text-white/60 ml-0.5">{device.rssi} dBm</span>}
              </span>
            )}
          </div>
          {isClosest && (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-white/25 border border-white/40 rounded-full px-2.5 py-0.5 text-xs font-bold text-white">
              <Navigation className="w-3 h-3" /> Closest to you
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">

        {/* Temperature + Humidity — always visible */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Thermometer className="w-4 h-4 text-orange-500" />
              <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Temp</span>
            </div>
            <div className="text-2xl font-black text-orange-900">
              {device.temperature != null ? fmt(device.temperature) + '°C' : '—'}
            </div>
            <div className="mt-1 h-1 bg-orange-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full"
                style={{ width: `${Math.min(100, ((device.temperature ?? 0) / 40) * 100)}%` }} />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Droplets className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Humidity</span>
            </div>
            <div className="text-2xl font-black text-blue-900">
              {device.humidity != null ? fmt(device.humidity, 0) + '%' : '—'}
            </div>
            <div className="mt-1 h-1 bg-blue-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-400 to-cyan-500 rounded-full"
                style={{ width: `${device.humidity ?? 0}%` }} />
            </div>
          </div>
        </div>

        {/* Lock banner — login for all other data */}
        {!isAuthenticated && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
            <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold text-amber-900">Login</span> to access wind, rainfall, pressure, UV index, light intensity &amp; historical charts.
            </p>
          </div>
        )}

        {/* Action button */}
        <button
          onClick={(e) => { e.preventDefault(); onViewDashboard(device); }}
          className="w-full bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-800 hover:to-blue-600 text-white font-bold py-3 px-5 rounded-xl shadow hover:shadow-lg transition-all flex items-center justify-center gap-2 group"
        >
          <BarChart2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
          <span>{isAuthenticated ? 'View Full Dashboard' : 'Login to View Dashboard'}</span>
        </button>
      </div>

      <div className="h-1 bg-gradient-to-r from-blue-700 via-blue-500 to-cyan-500"></div>
    </div>
  );
};

// Map Controller Component
const MapController = ({ devices, soilDevices, zoomToLocation, onZoomComplete }) => {
  const map = useMap();
  const hasFit = useRef(false);

  // Auto-fit bounds to ALL markers — runs once on first meaningful data load
  useEffect(() => {
    if (hasFit.current || zoomToLocation) return;

    const positions = [];

    // Weather station positions
    devices.forEach(d => {
      if (d.lat != null && d.lng != null) positions.push([d.lat, d.lng]);
    });

    // Soil sensor positions (with same fallback logic as render)
    soilDevices.forEach(d => {
      let lat = d.location?.latitude;
      let lng = d.location?.longitude;
      if ((lat == null || lng == null) && d.weatherStationId) {
        const ws = devices.find(w =>
          w.stationId === d.weatherStationId || w.id === d.weatherStationId
        );
        if (ws?.lat != null) { lat = ws.lat; lng = ws.lng; }
      }
      if (lat != null && lng != null) positions.push([lat, lng]);
    });

    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 10 });
      hasFit.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, soilDevices]);

  // Zoom to a specific marker ("Show Me nearest" button)
  useEffect(() => {
    if (!zoomToLocation) return;
    map.setView([zoomToLocation.lat, zoomToLocation.lng], 13, { animate: true, duration: 1 });
    if (onZoomComplete) setTimeout(onZoomComplete, 1000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomToLocation]);

  return null;
};

// Soil sensor popup card
const SoilStationCard = ({ device }) => {
  // latestReading is { soilData: {...}, receivedAt: '...' }
  const sd = device.latestReading?.soilData || null;
  const fmt = (v, dec = 1) => (v != null && !isNaN(Number(v))) ? Number(v).toFixed(dec) : '—';
  const hasData = sd !== null;

  const params = [
    { label: 'Moisture',    key: 'hum',   dec: 0, unit: '%',     color: '#3b82f6' },
    { label: 'Soil Temp',   key: 'temp',  dec: 1, unit: '°C',    color: '#ef4444' },
    { label: 'pH',          key: 'ph',    dec: 1, unit: '',       color: '#10b981' },
    { label: 'Conductivity',key: 'ec',    dec: 0, unit: 'µS/cm', color: '#8b5cf6' },
    { label: 'Nitrogen',    key: 'nitro', dec: 0, unit: 'mg/kg', color: '#84cc16' },
    { label: 'Phosphorus',  key: 'phos',  dec: 0, unit: 'mg/kg', color: '#f97316' },
    { label: 'Potassium',   key: 'pota',  dec: 0, unit: 'mg/kg', color: '#eab308' },
    { label: 'Battery',     key: 'battery_mv', dec: 0, unit: 'mV', color: '#6b7280' },
  ];

  const receivedAt = device.latestReading?.receivedAt
    ? new Date(device.latestReading.receivedAt).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <div className="min-w-[280px] bg-white rounded-2xl overflow-hidden shadow-2xl">
      {/* Header */}
      <div className="bg-gradient-to-br from-green-800 via-green-700 to-emerald-600 p-4">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-white font-bold text-base leading-tight flex-1 pr-2">{device.name}</h3>
          <div className={`px-2 py-0.5 rounded-full text-xs font-bold shadow ${
            device.status === 'active' ? 'bg-emerald-300 text-emerald-900' : 'bg-rose-400 text-rose-900'
          }`}>
            {device.status === 'active' ? 'ACTIVE' : 'OFFLINE'}
          </div>
        </div>
        <p className="text-green-200 text-xs">Soil Sensor · {device.depth_cm ?? 10} cm depth</p>
        {device.weatherStationId && (
          <p className="text-green-300 text-xs mt-0.5">Co-located: {device.weatherStationId}</p>
        )}
        {receivedAt && (
          <p className="text-green-200 text-[10px] mt-1 opacity-80">Last reading: {receivedAt}</p>
        )}
      </div>

      {!hasData ? (
        <div className="p-5 text-center text-sm text-gray-400">
          <div className="text-2xl mb-1">🌱</div>
          No readings received yet
        </div>
      ) : (
        <div className="p-3 grid grid-cols-2 gap-2">
          {params.map(({ label, key, dec, unit, color }) => {
            const val = fmt(sd[key], dec);
            const hasVal = val !== '—';
            return (
              <div key={key} className="bg-gray-50 border border-gray-100 rounded-lg p-2">
                <div className="flex items-center gap-1 mb-0.5">
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
                  <span className="text-[9px] font-bold uppercase tracking-wide text-gray-500">{label}</span>
                </div>
                <div className={`text-sm font-black ${hasVal ? 'text-gray-900' : 'text-gray-300'}`}>
                  {val}<span className="text-xs font-medium text-gray-400 ml-0.5">{unit}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="h-1 bg-gradient-to-r from-green-700 to-emerald-500" />
    </div>
  );
};

// Main DevicesMap Component
const DevicesMap = ({ onLoginSuccess, onStationSelect, allowedDeviceIds = null, userRole, onNearestFound, onNavigate }) => {
  const [devices, setDevices] = useState([]);
  const [soilDevices, setSoilDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingStationSelect, setPendingStationSelect] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [showNearestPrompt, setShowNearestPrompt] = useState(false);
  const [closestStation, setClosestStation] = useState(null);
  const [zoomToLocation, setZoomToLocation] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [soilFilter, setSoilFilter] = useState('all');
  const [showWeather, setShowWeather] = useState(true);
  const [showSoil, setShowSoil] = useState(true);
  const markerRefs = useRef({});

  const isAuthenticated = !!localStorage.getItem('authToken');

  useEffect(() => {
    loadDevices();
    const interval = setInterval(loadDevices, 5 * 60 * 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (navigator.geolocation && devices.length > 0 && !userLocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userLoc);
          const closest = findClosestStation(userLoc, devices);
          if (closest) {
            setClosestStation(closest);
            setShowNearestPrompt(true);
            onNearestFound?.(closest);
          }
        },
        (error) => console.log('Location not available:', error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, userLocation]);

  const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const findClosestStation = (location, stationsList) => {
    let closest = null;
    let minDistance = Infinity;

    stationsList
      .filter(s => s.lat != null && s.lng != null)
      .forEach(station => {
        const distance = haversineKm(location.lat, location.lng, station.lat, station.lng);
        if (distance < minDistance) {
          minDistance = distance;
          closest = { ...station, distance };
        }
      });

    return closest;
  };

  const loadDevices = async () => {
    try {
      setLoading(true);

      const [devicesData, soilData] = await Promise.allSettled([
        fetchWithRetry(fetchWeatherDevices),
        fetchSoilDevices(),
      ]);

      if (devicesData.status === 'fulfilled') {
        const all = devicesData.value;
        const filtered = allowedDeviceIds === null
          ? all
          : all.filter(d => allowedDeviceIds.includes(d.id));
        console.log(`✅ Weather devices: ${all.length}${filtered.length !== all.length ? `, showing ${filtered.length}` : ''}`);
        setDevices(filtered);
      }

      if (soilData.status === 'fulfilled') {
        console.log(`✅ Soil sensors: ${soilData.value.length} total`);
        setSoilDevices(soilData.value);
      }
    } catch (err) {
      console.error('Failed to load devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadDevices();
    setIsRefreshing(false);
  };

  const createDeviceIcon = (device) => {
    const color = device.status === 'online' ? '#10b981' :
                 device.status === 'warning' ? '#f59e0b' : '#ef4444';

    return new L.DivIcon({
      html: `<div style="width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg, ${color}, ${color}dd);border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="width:10px;height:10px;border-radius:50%;background-color:white"></div></div>`,
      iconSize: [28, 28],
      iconAnchor: [14, 14],
      popupAnchor: [0, -14],
      className: 'custom-marker'
    });
  };

  const createSoilIcon = (device) => {
    const active = device.status === 'active';
    const fill   = active ? '#15803d' : '#6b7280';
    const shadow = active ? 'rgba(21,128,61,0.5)' : 'rgba(0,0,0,0.25)';
    return new L.DivIcon({
      html: `
        <div style="width:30px;height:40px;filter:drop-shadow(0 4px 8px ${shadow})">
          <svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Teardrop pin body -->
            <path d="M15 1C8.1 1 2.5 6.6 2.5 13.5C2.5 23.5 15 38 15 38S27.5 23.5 27.5 13.5C27.5 6.6 21.9 1 15 1Z" fill="${fill}" stroke="white" stroke-width="1.6"/>
            <!-- Wheat stem -->
            <line x1="15" y1="23" x2="15" y2="9" stroke="white" stroke-width="1.4" stroke-linecap="round"/>
            <!-- Left grains -->
            <ellipse cx="11.5" cy="18" rx="3.2" ry="1.5" fill="white" opacity="0.95" transform="rotate(-42 11.5 18)"/>
            <ellipse cx="11.5" cy="14.5" rx="3" ry="1.4" fill="white" opacity="0.80" transform="rotate(-38 11.5 14.5)"/>
            <!-- Right grains -->
            <ellipse cx="18.5" cy="18" rx="3.2" ry="1.5" fill="white" opacity="0.95" transform="rotate(42 18.5 18)"/>
            <ellipse cx="18.5" cy="14.5" rx="3" ry="1.4" fill="white" opacity="0.80" transform="rotate(38 18.5 14.5)"/>
            <!-- Top grain -->
            <ellipse cx="15" cy="9" rx="2" ry="3" fill="white" opacity="0.95"/>
          </svg>
        </div>`,
      iconSize: [30, 40],
      iconAnchor: [15, 40],
      popupAnchor: [0, -42],
      className: '',
    });
  };

  const handleViewDashboard = (device) => {
    if (isAuthenticated && onStationSelect) {
      onStationSelect(device.stationId || device.id, {
        name: device.name,
        location: device.location,
        altitude: device.altitude,
        lat: device.lat,
        lng: device.lng,
        stationId: device.stationId || device.id
      });
    } else {
      setPendingStationSelect(device);
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = () => {
    onLoginSuccess();

    if (pendingStationSelect && onStationSelect) {
      setTimeout(() => {
        onStationSelect(pendingStationSelect.stationId || pendingStationSelect.id, {
          name: pendingStationSelect.name,
          location: pendingStationSelect.location,
          altitude: pendingStationSelect.altitude,
          lat: pendingStationSelect.lat,
          lng: pendingStationSelect.lng,
          stationId: pendingStationSelect.stationId || pendingStationSelect.id
        });
        setPendingStationSelect(null);
      }, 100);
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="map-view relative w-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <RefreshCw className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-700 font-bold text-xl">Loading Weather Stations...</p>
          <p className="text-sm text-gray-500 mt-2">Fetching all stations from API</p>
        </div>
      </div>
    );
  }

  // User has no assigned stations yet — show getting-started guide
  if (!loading && allowedDeviceIds !== null && allowedDeviceIds.length === 0) {
    const steps = [
      {
        Icon: CheckCircle,
        iconColor: '#16a34a', bg: '#f0fdf4', border: '#86efac',
        title: 'Account created',
        desc: "You're registered and logged in.",
        done: true,
      },
      {
        Icon: MessageSquare,
        iconColor: '#1D72B8', bg: '#eff6ff', border: '#bfdbfe',
        title: 'Request station access',
        desc: 'Send a message to the admin to get weather stations assigned to your account.',
        action: onNavigate ? () => onNavigate('messages') : null,
        actionLabel: 'Open Messages',
      },
      {
        Icon: CreditCard,
        iconColor: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe',
        title: 'Choose a plan (optional)',
        desc: 'Basic is free. Upgrade to unlock more stations, analytics & API access.',
        action: onNavigate ? () => onNavigate('subscription') : null,
        actionLabel: 'View Plans',
      },
      {
        Icon: Radio,
        iconColor: '#0891b2', bg: '#ecfeff', border: '#a5f3fc',
        title: 'Access live station data',
        desc: "Once assigned, your stations will appear here on the map with live readings.",
        done: false,
      },
    ];

    return (
      <div className="w-full min-h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">Getting Started</h2>
            <p className="text-sm text-gray-500">Follow these steps to access your weather station data.</p>
          </div>

          {/* Steps */}
          <div className="flex flex-col gap-3 mb-6">
            {steps.map(({ Icon, iconColor, bg, border, title, desc, done, action, actionLabel }, i) => (
              <div key={i} style={{ background: bg, border: `1px solid ${border}` }}
                className="rounded-xl p-4 flex gap-3 items-start">
                <div style={{ background: '#fff', border: `1.5px solid ${border}` }}
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon style={{ color: iconColor }} className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-bold text-sm text-gray-900">{title}</p>
                    {done && (
                      <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Done</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                  {action && (
                    <button onClick={action}
                      style={{ background: iconColor }}
                      className="mt-2 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:opacity-90 transition-opacity">
                      {actionLabel} →
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-400">
            Need help? Send a message to the administrator via the Messages tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-view relative w-full">

      {/* Map legend — bottom left above ticker */}
      <div className="absolute bottom-10 left-3 z-[1000] bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg flex items-center gap-4 text-xs text-gray-700">
        <div className="flex items-center gap-1.5">
          <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#10b981', border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
          <span className="font-medium">Weather Station</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="11" height="15" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 1C8.1 1 2.5 6.6 2.5 13.5C2.5 23.5 15 38 15 38S27.5 23.5 27.5 13.5C27.5 6.6 21.9 1 15 1Z" fill="#15803d" stroke="white" stroke-width="2"/>
          </svg>
          <span className="font-medium">Soil Sensor</span>
        </div>
      </div>

      {/* Controls — top right */}
      <div className="absolute top-4 right-4 z-[1000] flex flex-col space-y-2">
        <button onClick={handleRefresh} disabled={isRefreshing} className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
          <div className="flex items-center space-x-2">
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">{isRefreshing ? 'Updating...' : 'Refresh'}</span>
          </div>
        </button>

        {/* Filter panel — layer toggles + status filters */}
        <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg min-w-[170px]">

          {/* Layer visibility toggles */}
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">Layers</p>
          <div className="space-y-1 mb-3">
            {/* Weather toggle */}
            <button
              onClick={() => setShowWeather(v => !v)}
              className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg border transition-all ${
                showWeather
                  ? 'bg-blue-50 border-blue-200 text-blue-800'
                  : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}
            >
              <div className="flex items-center gap-2">
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: showWeather ? '#3b82f6' : '#d1d5db', border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', flexShrink: 0 }} />
                <span className="text-xs font-semibold">Weather</span>
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${showWeather ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-400'}`}>
                {devices.length}
              </span>
            </button>
            {/* Soil toggle */}
            <button
              onClick={() => setShowSoil(v => !v)}
              className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg border transition-all ${
                showSoil
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-gray-50 border-gray-200 text-gray-400'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg width="9" height="12" viewBox="0 0 30 40" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M15 1C8.1 1 2.5 6.6 2.5 13.5C2.5 23.5 15 38 15 38S27.5 23.5 27.5 13.5C27.5 6.6 21.9 1 15 1Z" fill={showSoil ? '#15803d' : '#d1d5db'}/>
                </svg>
                <span className="text-xs font-semibold">Soil</span>
              </div>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${showSoil ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-400'}`}>
                {soilDevices.length}
              </span>
            </button>
          </div>

          <div className="border-t border-gray-200 my-2" />

          {/* Weather status filter — only when layer is on */}
          {showWeather && (
            <>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: '#3b82f6' }} />
                  Weather
                </p>
                {statusFilter !== 'all' && (
                  <button onClick={() => setStatusFilter('all')} className="text-[10px] text-blue-500 font-semibold">clear</button>
                )}
              </div>
              <div className="space-y-1 mb-3">
                {[
                  { key: 'online',  label: 'Online',  dot: 'bg-green-500',  pulse: true  },
                  { key: 'warning', label: 'Warning', dot: 'bg-yellow-500', pulse: false },
                  { key: 'offline', label: 'Offline', dot: 'bg-red-500',    pulse: false },
                ].map(({ key, label, dot, pulse }) => {
                  const count = devices.filter(d => d.status === key).length;
                  const active = statusFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setStatusFilter(active ? 'all' : key)}
                      className={`w-full flex items-center justify-between space-x-3 px-2 py-1 rounded-md transition-colors ${
                        active ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${dot} ${pulse && !active ? 'animate-pulse' : ''}`} />
                        <span className={`text-xs ${active ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>{label}</span>
                      </div>
                      <span className={`text-xs font-bold ${active ? 'text-gray-800' : 'text-gray-500'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Soil status filter — only when layer is on */}
          {showSoil && (
            <>
              {showWeather && <div className="border-t border-gray-200 my-2" />}
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <svg width="7" height="10" viewBox="0 0 30 40" fill="none"><path d="M15 1C8.1 1 2.5 6.6 2.5 13.5C2.5 23.5 15 38 15 38S27.5 23.5 27.5 13.5C27.5 6.6 21.9 1 15 1Z" fill="#15803d"/></svg>
                  Soil
                </p>
                {soilFilter !== 'all' && (
                  <button onClick={() => setSoilFilter('all')} className="text-[10px] text-green-600 font-semibold">clear</button>
                )}
              </div>
              <div className="space-y-1">
                {[
                  { key: 'active',   label: 'Active',   color: '#15803d' },
                  { key: 'inactive', label: 'Inactive',  color: '#6b7280' },
                ].map(({ key, label, color }) => {
                  const count = soilDevices.filter(d => d.status === key).length;
                  const active = soilFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setSoilFilter(active ? 'all' : key)}
                      className={`w-full flex items-center justify-between space-x-3 px-2 py-1 rounded-md transition-colors ${
                        active ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <svg width="8" height="11" viewBox="0 0 30 40" fill="none" style={{ flexShrink: 0 }}><path d="M15 1C8.1 1 2.5 6.6 2.5 13.5C2.5 23.5 15 38 15 38S27.5 23.5 27.5 13.5C27.5 6.6 21.9 1 15 1Z" fill={color}/></svg>
                        <span className={`text-xs ${active ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>{label}</span>
                      </div>
                      <span className={`text-xs font-bold ${active ? 'text-gray-800' : 'text-gray-500'}`}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Nearest Station Prompt */}
      {showNearestPrompt && closestStation && (
        <div className="absolute top-24 left-4 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-xl border border-blue-200 p-4 max-w-sm">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Navigation className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">Nearest Station</h3>
            </div>
            <button onClick={() => setShowNearestPrompt(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <p className="text-sm text-gray-700 mb-2">
            <strong>{closestStation.name}</strong> (~{closestStation.distance.toFixed(1)} km away)
          </p>

          <div className="flex space-x-2">
            <button
              onClick={() => {
                setZoomToLocation({ lat: closestStation.lat, lng: closestStation.lng });
                setShowNearestPrompt(false);
                setTimeout(() => {
                  markerRefs.current[closestStation.id]?.openPopup();
                }, 1200);
              }}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Show Me
            </button>
            <button
              onClick={() => setShowNearestPrompt(false)}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 text-sm font-medium"
            >
              Later
            </button>
          </div>
        </div>
      )}

      {/* Map */}
      <MapContainer 
        center={[0.4, 34.3]} 
        zoom={8} 
        style={{ height: '100vh', width: '100vw' }} 
        zoomControl={false}
      >
        <MapController
          devices={devices}
          soilDevices={soilDevices}
          zoomToLocation={zoomToLocation}
          onZoomComplete={() => setZoomToLocation(null)}
        />
        <TileLayer 
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" 
          attribution='&copy; OpenStreetMap' 
        />
        
        {showWeather && (statusFilter === 'all' ? devices : devices.filter(d => d.status === statusFilter))
          .filter(d => d.lat != null && d.lng != null).map((device) => (
          <Marker
            key={device.id}
            position={[device.lat, device.lng]}
            icon={createDeviceIcon(device)}
            ref={el => { markerRefs.current[device.id] = el; }}
          >
            <Popup maxWidth={360}>
              <BeautifulStationCard
                device={device}
                isAuthenticated={isAuthenticated}
                onViewDashboard={handleViewDashboard}
                isClosest={closestStation?.id === device.id}
                isSuperAdmin={userRole === 'super_admin'}
              />
            </Popup>
          </Marker>
        ))}

        {/* Soil sensor markers */}
        {showSoil && soilDevices.map((device) => {
          // Use explicit location; fall back to co-located weather station position
          let lat = device.location?.latitude;
          let lng = device.location?.longitude;
          if ((lat == null || lng == null) && device.weatherStationId) {
            const ws = devices.find(d =>
              d.stationId === device.weatherStationId || d.id === device.weatherStationId
            );
            if (ws?.lat != null) { lat = ws.lat; lng = ws.lng; }
          }
          if (lat == null || lng == null) return null;
          if (soilFilter !== 'all' && device.status !== soilFilter) return null;
          return (
            <Marker
              key={`soil-${device.deviceId}`}
              position={[lat, lng]}
              icon={createSoilIcon(device)}
            >
              <Popup maxWidth={300}>
                <SoilStationCard device={device} />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      <EnhancedAuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setPendingStationSelect(null);
        }}
        onLoginSuccess={handleAuthSuccess}
      />

      {/* ── Station status ticker ── */}
      {devices.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 z-[1000] bg-gray-950/85 backdrop-blur-sm border-t border-gray-700/60 h-9 flex items-center overflow-hidden">
          {/* Pinned label */}
          <div className="flex-shrink-0 flex items-center space-x-1.5 px-3 border-r border-gray-700/60 h-full bg-gray-900/80">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            <span className="text-blue-400 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap">Live Network</span>
          </div>
          {/* Scrolling track */}
          <div className="flex-1 overflow-hidden relative">
            <div
              className="flex items-center space-x-0 whitespace-nowrap"
              style={{
                animation: 'ticker-scroll 40s linear infinite',
                display: 'inline-flex',
              }}
            >
              {/* Duplicate for seamless loop */}
              {[...devices, ...devices].map((d, i) => {
                const isOnline  = (d.status || '').toLowerCase() === 'online';
                const isWarning = (d.status || '').toLowerCase() === 'warning';
                const dotColor  = isOnline ? 'bg-green-400' : isWarning ? 'bg-yellow-400' : 'bg-red-400';
                const label     = isOnline ? 'LIVE' : isWarning ? 'WARN' : 'DOWN';
                const labelColor= isOnline ? 'text-green-400' : isWarning ? 'text-yellow-400' : 'text-red-400';
                const temp      = d.currentReading?.temperature ?? d.temperature;
                const hum       = d.currentReading?.humidity ?? d.humidity;
                return (
                  <span key={i} className="inline-flex items-center space-x-1.5 px-4 border-r border-gray-700/40 h-9">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor} ${isOnline ? 'animate-pulse' : ''}`} />
                    <span className="text-gray-300 text-[11px] font-medium">{d.name || d.device_name || `Station ${d.id}`}</span>
                    <span className={`text-[10px] font-bold ${labelColor}`}>{label}</span>
                    {temp != null && temp > 0 && (
                      <span className="text-gray-400 text-[10px]">{parseFloat(temp).toFixed(1)}°C</span>
                    )}
                    {hum != null && hum > 0 && (
                      <span className="text-gray-500 text-[10px]">{parseFloat(hum).toFixed(0)}%</span>
                    )}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default DevicesMap;